import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import HeroSection from './components/HeroSection';
import PhilosophySection from './components/PhilosophySection';
import StepsSection from './components/StepsSection';
import DifferentialsSection from './components/DifferentialsSection';
import GovernanceSection from './components/GovernanceSection';
import { useScrollReveal } from '../../hooks/useScrollReveal';

export default function MethodPage() {
  const { t } = useTranslation('method');
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollReveal({ threshold: 0.2 });

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Definir meta tags SEO
    document.title = "Metodologia FGT | Engenharia Patrimonial com Rigor Técnico";
    
    // Adicionar Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Metodologia - FGT Capital",
      "description": "Conheça nossa metodologia proprietária de engenharia patrimonial: método estruturado, modelagem de cenários e execução disciplinada para decisões patrimoniais.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/metodo`,
      "about": {
        "@type": "Thing",
        "name": "Metodologia FGT",
        "description": "Processo estruturado em 4 etapas para engenharia patrimonial"
      },
      "mainEntity": {
        "@type": "HowTo",
        "name": "Metodologia FGT de Engenharia Patrimonial",
        "description": "Processo estruturado para decisões patrimoniais via consórcios regulados",
        "step": [
          {
            "@type": "HowToStep",
            "name": "Diagnóstico Patrimonial",
            "text": "Análise completa do perfil patrimonial e objetivos do cliente"
          },
          {
            "@type": "HowToStep", 
            "name": "Modelagem de Cenários",
            "text": "Estruturação de alternativas financeiras personalizadas"
          },
          {
            "@type": "HowToStep",
            "name": "Execução Estruturada",
            "text": "Implementação disciplinada da estratégia patrimonial"
          },
          {
            "@type": "HowToStep",
            "name": "Acompanhamento Contínuo",
            "text": "Monitoramento e ajustes durante todo o processo"
          }
        ],
        "totalTime": "PT1H",
        "tool": ["Consórcios Regulados", "Análise Financeira", "Planejamento Patrimonial"]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F6F2EC]">

      <HeroSection />
      <PhilosophySection />
      <StepsSection />
      <DifferentialsSection />
      <GovernanceSection />

      {/* CTA Final */}
      <section className="relative py-32 px-6 overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/b73fa68321b549d27d910beeb442460c.jpg"
            alt="CTA background"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0B]/95 via-[#0B0B0B]/85 to-[#0B0B0B]/60"></div>

        <div ref={ctaRef} className="relative z-10 max-w-4xl mx-auto text-center">
          <p
            className="text-xs font-semibold tracking-[0.25em] uppercase text-[#F97700] mb-6 transition-all duration-700"
            style={{
              opacity: ctaVisible ? 1 : 0,
              transform: ctaVisible ? 'translateY(0)' : 'translateY(20px)',
              transitionDelay: '0ms',
            }}
          >
            {t('cta.label', 'Próximo passo')}
          </p>
          <h2
            className="text-4xl md:text-5xl font-semibold text-white leading-[1.1] mb-6 tracking-tight transition-all duration-700"
            style={{
              opacity: ctaVisible ? 1 : 0,
              transform: ctaVisible ? 'translateY(0)' : 'translateY(24px)',
              transitionDelay: '120ms',
            }}
          >
            {t('cta.title')}
          </h2>
          <p
            className="text-white/60 mb-12 text-base max-w-xl mx-auto leading-relaxed transition-all duration-700"
            style={{
              opacity: ctaVisible ? 1 : 0,
              transform: ctaVisible ? 'translateY(0)' : 'translateY(20px)',
              transitionDelay: '240ms',
            }}
          >
            {t('cta.subtitle')}
          </p>
          <div
            className="flex flex-col sm:flex-row gap-4 justify-center transition-all duration-700"
            style={{
              opacity: ctaVisible ? 1 : 0,
              transform: ctaVisible ? 'translateY(0)' : 'translateY(20px)',
              transitionDelay: '360ms',
            }}
          >
            <a
              href="/diagnostico"
              className="inline-flex items-center justify-center bg-[#F97700] text-white px-10 py-4 text-sm font-semibold tracking-wide hover:bg-[#E56E00] transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('cta.button')}
            </a>
            <a
              href="https://wa.me/5511930322408"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-10 py-4 text-sm font-medium tracking-wide hover:border-white/60 hover:bg-white/5 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-base"></i>
              {t('cta.secondary')}
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}